// Role management utilities for frontend
import baseUrl from "../Components/Configs";

// Role hierarchy (matches backend)
export const ROLE_HIERARCHY = {
  'masteruser': 100,
  'superuser': 90,
  'data_protection_officer': 85,
  'child_protection_officer': 85,
  'admin': 80,
  'compliance_officer': 80,
  'pocso_committee_member': 75,
  'posh_committee_member': 75,
  'principal': 70,
  'teacher': 60,
  'crm': 55,
  'librarian': 50,
  'accountant': 50,
  'clerk': 40,
  'receptionist': 35,
  'auditor_external': 30,
  'other': 30,
  'guest': 20,
  'parent': 15,
  'guardian': 10,
  'student': 5
};

// Get priority level for a role
export const getRolePriority = (role) => {
  return ROLE_HIERARCHY[role] || 0;
};

// Get highest priority role from user's roles
export const getHighestPriorityRole = (userRoles) => {
  if (!userRoles || userRoles.length === 0) return null;
  
  let highestRole = null;
  let highestPriority = -1;
  
  userRoles.forEach(role => {
    const priority = getRolePriority(role);
    if (priority > highestPriority) {
      highestPriority = priority;
      highestRole = role;
    }
  });
  
  return highestRole;
};

// Check if user has specific role
export const hasRole = (userRoles, requiredRole) => {
  if (!userRoles) return false;
  return userRoles.includes(requiredRole);
};

// Check if user has any of the required roles
export const hasAnyRole = (userRoles, requiredRoles) => {
  if (!userRoles || !requiredRoles) return false;
  return requiredRoles.some(role => userRoles.includes(role));
};

// Check if user's highest role meets minimum priority requirement
export const hasMinimumRolePriority = (userRoles, minimumRole) => {
  const userHighestRole = getHighestPriorityRole(userRoles);
  if (!userHighestRole) return false;
  
  const userPriority = getRolePriority(userHighestRole);
  const requiredPriority = getRolePriority(minimumRole);
  
  return userPriority >= requiredPriority;
};

// Enhanced role checking with priority consideration
export const hasRoleWithPriority = (userRoles, requiredRoles) => {
  if (!userRoles || !requiredRoles) return false;
  
  // Check if user has any of the required roles directly
  const hasDirectRole = hasAnyRole(userRoles, requiredRoles);
  if (hasDirectRole) return true;
  
  // Check if user's highest priority role meets or exceeds any required role's priority
  const userHighestRole = getHighestPriorityRole(userRoles);
  const userPriority = getRolePriority(userHighestRole);
  
  return requiredRoles.some(role => {
    const requiredPriority = getRolePriority(role);
    return userPriority >= requiredPriority;
  });
};

// Get user's comprehensive role information from backend
export const fetchUserRoleInfo = async (token) => {
  try {
    const response = await fetch(`${baseUrl}/v1/userroute/getuserroleinfo`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const result = await response.json();
      return result.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user role info:", error);
    return null;
  }
};

// Get role hierarchy from backend
export const fetchRoleHierarchy = async (token) => {
  try {
    const response = await fetch(`${baseUrl}/v1/userroute/getrolehierarchy`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const result = await response.json();
      return result.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching role hierarchy:", error);
    return null;
  }
};

// Update localStorage with comprehensive role information
export const updateUserRoleStorage = (userRoleInfo) => {
  if (!userRoleInfo) return;
  
  // Determine the highest priority role from the provided info
  const highestRole = userRoleInfo.highestPriorityRole || 
                     getHighestPriorityRole(userRoleInfo.allRoles) || 
                     userRoleInfo.primaryRole;
  
  localStorage.setItem("userRole", highestRole); // Set to highest priority role for backward compatibility
  localStorage.setItem("userRoles", JSON.stringify(userRoleInfo.allRoles || [userRoleInfo.primaryRole]));
  localStorage.setItem("userHighestPriorityRole", highestRole);
  localStorage.setItem("userHighestPriorityLevel", userRoleInfo.highestPriorityLevel?.toString() || getRolePriority(highestRole).toString());
};

// Get the current effective role (highest priority role) for the user
export const getCurrentUserRole = () => {
  return localStorage.getItem("userRole");
};

// Get the current effective role priority for the user
export const getCurrentUserRolePriority = () => {
  const currentRole = getCurrentUserRole();
  return getRolePriority(currentRole);
};

// Check if current user has minimum role priority (simplified version)
export const currentUserHasMinimumPriority = (minimumRole) => {
  const userRoles = getUserRolesFromStorage();
  return hasMinimumRolePriority(userRoles, minimumRole);
};

// Get user roles from localStorage
export const getUserRolesFromStorage = () => {
  try {
    const roles = localStorage.getItem("userRoles");
    const primaryRole = localStorage.getItem("userRole");
    
    if (roles) {
      return JSON.parse(roles);
    } else if (primaryRole) {
      return [primaryRole];
    }
    return [];
  } catch (error) {
    console.error("Error parsing user roles from storage:", error);
    const primaryRole = localStorage.getItem("userRole");
    return primaryRole ? [primaryRole] : [];
  }
};

// Get highest priority role from localStorage
export const getHighestPriorityRoleFromStorage = () => {
  return localStorage.getItem("userHighestPriorityRole") || localStorage.getItem("userRole");
};

// Clear all role-related data from localStorage
export const clearRoleStorage = () => {
  localStorage.removeItem("userRole");
  localStorage.removeItem("userRoles");
  localStorage.removeItem("userHighestPriorityRole");
  localStorage.removeItem("userHighestPriorityLevel");
}; 
